/**
 * STAGING-LAUNCH-READINESS-002-R2-R8-R1 / MANAGER-ORDERS-141-R2 —
 * post-pay referral share with three selectable message variants.
 * CUSTOMER-R4.4 — compact card + sheet so referral stays secondary.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { copyText } from "./contact-manager-v2.js";
import { emitEvent } from "./events-v2.js";
import { StoreV2Icon } from "./StoreV2Icon.js";
import { t } from "./store-v2-ui-copy.js";
import {
  buildInviteSharePayload,
  getInviteCard,
  INVITE_MESSAGE_CARDS,
  openTelegramShareSync,
  paidShareBody,
  paidShareTitle,
  REFERRAL_REWARDS_UI_ENABLED,
  type InviteCardId,
  type InviteSharePayload,
} from "./referral-share-v2.js";

export interface ReferralShareData {
  code: string;
  token: string;
  url: string;
  preparedMessageId: string | null;
  preparedMessageExpiresAt: string | null;
}

function ReferralSharePanel({
  lang,
  payload,
  cardId,
  setCardId,
  copied,
  fallbackOpen,
  runShare,
  runCopy,
}: {
  lang: StoreV2Lang;
  payload: InviteSharePayload;
  cardId: InviteCardId;
  setCardId: (id: InviteCardId) => void;
  copied: boolean;
  fallbackOpen: boolean;
  runShare: () => void;
  runCopy: () => void;
}) {
  const selectedCard = getInviteCard(cardId);
  return (
    <>
      <div className="sv2-referral-variants" data-testid="sv2-referral-variants" role="radiogroup">
        {INVITE_MESSAGE_CARDS.map((card) => {
          const selected = card.id === cardId;
          return (
            <button
              key={card.id}
              type="button"
              role="radio"
              aria-checked={selected}
              className={`sv2-referral-variant${selected ? " is-selected" : ""}`}
              data-testid={`sv2-referral-variant-${card.id}`}
              onClick={() => {
                setCardId(card.id);
                emitEvent("referral_variant_selected", { template_key: card.id });
              }}
            >
              {lang === "en" ? card.titleEn : card.titleUk}
            </button>
          );
        })}
      </div>

      <p className="sv2-referral-preview" data-testid="sv2-referral-preview">
        {payload.previewText || (lang === "en" ? selectedCard.bodyEn : selectedCard.bodyUk)}
      </p>

      <div className="sv2-referral-actions sv2-referral-actions-stack" data-testid="sv2-referral-actions">
        <button
          type="button"
          className="sv2-cta sv2-action-primary sv2-referral-action sv2-referral-share-primary"
          data-testid="sv2-referral-share"
          onClick={runShare}
        >
          <StoreV2Icon name="telegram" size={20} />
          <span>{lang === "en" ? "✈️ Invite friends" : "✈️ Запросити друзів"}</span>
        </button>
        <button
          type="button"
          className="sv2-cta sv2-action-secondary sv2-referral-copy-btn"
          data-testid="sv2-referral-copy-link"
          onClick={runCopy}
        >
          {copied
            ? lang === "en"
              ? "Link copied ✓"
              : "Посилання скопійовано ✓"
            : lang === "en"
              ? "🔗 Copy link"
              : "🔗 Скопіювати посилання"}
        </button>
      </div>

      {fallbackOpen ? (
        <div className="sv2-referral-fallback" data-testid="sv2-referral-fallback" role="status">
          <p className="sv2-muted">
            {lang === "en"
              ? "Telegram share did not open. Copy the link instead."
              : "Не вдалося відкрити Telegram share. Скопіюйте посилання."}
          </p>
        </div>
      ) : null}
    </>
  );
}

export function ReferralCardV2({
  lang,
  referral,
  onShared,
  surface = "paid_bento",
  compact = false,
}: {
  lang: StoreV2Lang;
  referral: ReferralShareData | null;
  /** @deprecated R8 — create-on-click removed; share uses prebuilt payload. */
  onCreate?: () => void;
  /** @deprecated R8-R1 — rules not on primary paid CTA surface. */
  onOpenRules?: () => void;
  onShared?: () => void;
  surface?: "paid_bento" | "cabinet" | "other";
  compact?: boolean;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const shareGuard = useRef(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [fallbackOpen, setFallbackOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [cardId, setCardId] = useState<InviteCardId>("find");

  useEffect(() => {
    emitEvent("referral_card_impression", {
      surface,
      compact: compact ? 1 : 0,
      collapsed: compact ? 1 : 0,
    });
  }, [surface, compact]);

  const payload: InviteSharePayload = useMemo(
    () =>
      buildInviteSharePayload(lang, {
        personalReferralLink: referral?.url ?? null,
        referralCode: referral?.code ?? null,
        referralToken: referral?.token ?? null,
        cardId,
      }),
    [lang, referral?.url, referral?.code, referral?.token, cardId],
  );

  const runShare = () => {
    emitEvent("referral_share_clicked", {
      surface,
      template_key: cardId,
      has_referral_token: payload.hasReferralToken,
    });
    if (shareGuard.current) return;
    shareGuard.current = true;
    window.setTimeout(() => {
      shareGuard.current = false;
    }, 1200);

    emitEvent("referral_share_open_attempted", {
      surface,
      template_key: cardId,
      has_referral_token: payload.hasReferralToken,
      mode: "telegram_share_url",
    });
    const result = openTelegramShareSync(payload.shareUrl);
    if (!result.ok) {
      setFallbackOpen(true);
      emitEvent("referral_share_fallback_shown", {
        surface,
        template_key: cardId,
        via: result.via,
      });
      emitEvent("referral_share_error", {
        surface,
        template_key: cardId,
        via: result.via,
        mode: "telegram_share_url",
      });
      return;
    }
    setFallbackOpen(false);
    emitEvent("referral_share_sent", {
      surface,
      template_key: cardId,
      has_referral_token: payload.hasReferralToken,
      via: result.via,
      mode: "telegram_share_url",
    });
    onShared?.();
    window.setTimeout(() => cardRef.current?.focus(), 0);
  };

  const runCopy = () => {
    void copyText(payload.deepLink || payload.previewText).then((ok) => {
      setCopied(ok);
      emitEvent(ok ? "referral_link_copied" : "referral_share_error", {
        surface,
        template_key: cardId,
        mode: "copy_fallback",
        has_referral_token: payload.hasReferralToken,
      });
    });
  };

  const panel = (
    <ReferralSharePanel
      lang={lang}
      payload={payload}
      cardId={cardId}
      setCardId={setCardId}
      copied={copied}
      fallbackOpen={fallbackOpen}
      runShare={runShare}
      runCopy={runCopy}
    />
  );

  return (
    <div
      ref={cardRef}
      className={`sv2-info-card sv2-referral-card${compact ? " is-compact" : ""}`}
      data-testid="sv2-referral-card"
      data-compact={compact ? "1" : "0"}
      data-rewards-enabled={REFERRAL_REWARDS_UI_ENABLED ? "1" : "0"}
      tabIndex={-1}
    >
      {compact ? (
        <>
          <h3 className="sv2-referral-heading" data-testid="sv2-referral-honest-title">
            {t(lang, "compact_referral_title")}
          </h3>
          <p className="sv2-referral-body" data-testid="sv2-referral-honest-body">
            {t(lang, "compact_referral_body")}
          </p>
          <button
            type="button"
            className="sv2-cta sv2-secondary"
            data-testid="sv2-referral-open-sheet"
            onClick={() => {
              emitEvent("referral_open", { surface });
              setSheetOpen(true);
            }}
          >
            {t(lang, "compact_referral_cta")}
          </button>
        </>
      ) : (
        <>
          <h3 className="sv2-referral-heading" data-testid="sv2-referral-honest-title">
            {paidShareTitle(lang)}
          </h3>
          <p className="sv2-referral-body" data-testid="sv2-referral-honest-body">
            {paidShareBody(lang)}
          </p>
          {panel}
        </>
      )}

      {compact && sheetOpen ? (
        <div className="sv2-sheet-overlay" role="dialog" aria-modal="true" data-testid="sv2-referral-sheet">
          <button
            type="button"
            className="sv2-sheet-backdrop"
            aria-label={lang === "en" ? "Close" : "Закрити"}
            onClick={() => setSheetOpen(false)}
          />
          <div className="sv2-sheet-panel">
            <div className="sv2-sheet-handle" aria-hidden="true" />
            <h3 className="sv2-referral-heading">{t(lang, "compact_referral_title")}</h3>
            {panel}
          </div>
        </div>
      ) : null}
    </div>
  );
}
